import { useEffect, type RefObject } from "react";
import useDebounceCallback from "./useDebouncCallback";

type Options = {
  delay?: number;
};

interface UseInViewProps {
  childRef: RefObject<HTMLElement | null>;
  onInView: (inView: boolean) => void;
  options?: Options;
}

const useInView = ({ childRef, onInView, options }: UseInViewProps) => {
  const { delay } = options ?? {};
  const callback = delay ? useDebounceCallback(onInView, delay) : onInView;

  useEffect(() => {
    let prevInView = false;

    const childElement = childRef.current;
    if (!childElement) return;

    const checkInView = () => {
      const rect = childElement.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;

      if (inView !== prevInView) {
        callback(inView);
        prevInView = inView;
      }
    };

    checkInView();
    document.addEventListener("scroll", checkInView);

    return () => {
      document.removeEventListener("scroll", checkInView);
    };
  }, [childRef, callback]);
};

export default useInView;
